"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useApi } from "@/hooks/useApi";
import { useWorkspace } from "@/providers/WorkspaceContext";

// Define the types used for onboarding
export type OnboardingType = "chat" | "ui" | "admin" | "workspace";
export type OnboardingStatus = "not_started" | "in_progress" | "completed" | "skipped";

interface OnboardingTypeStatus {
  status: OnboardingStatus;
  requires_attention: boolean;
  conversation_id?: string;
}

interface UserOnboardingStatus {
  chat_onboarding: OnboardingTypeStatus;
  ui_onboarding: OnboardingTypeStatus;
  admin_onboarding?: OnboardingTypeStatus;
  workspace_onboarding?: OnboardingTypeStatus;
  onboarding_complete: boolean;
}

interface OnboardingContextType {
  isLoading: boolean;
  onboardingStatus: UserOnboardingStatus | null;
  isOnboardingRequired: boolean;
  requiresAttention: (type: OnboardingType) => boolean;
  completeOnboarding: (type: OnboardingType) => Promise<boolean>;
  skipOnboarding: (type: OnboardingType) => Promise<boolean>;
  updateProgress: (type: OnboardingType, progress: Record<string, any>) => Promise<boolean>;
  chatConversationId: string | null;
  startChatOnboarding: () => Promise<string | undefined>;
}

const OnboardingContext = createContext<OnboardingContextType>({
  isLoading: true,
  onboardingStatus: null,
  isOnboardingRequired: false,
  requiresAttention: () => false,
  completeOnboarding: async () => false,
  skipOnboarding: async () => false,
  updateProgress: async () => false,
  chatConversationId: null,
  startChatOnboarding: async () => undefined,
});

export const useOnboardingContext = () => useContext(OnboardingContext);

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const router = useRouter();
  const { currentWorkspace } = useWorkspace();
  const { request, isLoading: isApiLoading } = useApi();

  const [onboardingStatus, setOnboardingStatus] = useState<UserOnboardingStatus | null>(null);
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isLoading = isApiLoading || !isInitialized;

  // Helper function to check if a specific onboarding type requires attention
  const requiresAttention = (type: OnboardingType): boolean => {
    if (!onboardingStatus) return false;

    switch (type) {
    case "chat":
      // Don't set conversation ID here - we'll ensure it's set during initialization
      return onboardingStatus.chat_onboarding?.requires_attention || false;
    case "ui":
      return onboardingStatus.ui_onboarding?.requires_attention || false;
    case "admin":
      return onboardingStatus.admin_onboarding?.requires_attention || false;
    case "workspace":
      return onboardingStatus.workspace_onboarding?.requires_attention || false;
    default:
      return false;
    }
  };

  // Helper function to determine if any onboarding is required
  const isOnboardingRequired = (): boolean => {
    if (!onboardingStatus) return true;
    return !onboardingStatus.onboarding_complete;
  };

  // Function to get the user's onboarding status
  const getOnboardingStatus = async (workspaceId: string) => {
    try {
      // Note the trailing slash for API compatibility
      const url = `/api/onboarding/me/?workspace_id=${workspaceId}`;
      const response = await request(url);

      if (response?.data) {
        setOnboardingStatus(response.data);

        // Set chat conversation ID if it exists in the response
        if (response.data.chat_onboarding?.conversation_id) {
          setChatConversationId(response.data.chat_onboarding.conversation_id);
        }

        return response.data;
      }
    } catch (error) {
      console.error("Failed to get onboarding status:", error);
      throw error;
    }
  };

  // Function to check chat onboarding status
  const checkChatOnboardingStatus = async (workspaceId: string) => {
    try {
      // Note the trailing slash for API compatibility
      const url = `/api/onboarding/me/?workspace_id=${workspaceId}`;
      const response = await request(url);

      if (response?.data?.chat_onboarding?.conversation_id) {
        setChatConversationId(response.data.chat_onboarding.conversation_id);
      }

      return {
        hasOnboarding: response?.data?.has_onboarding || false,
        conversationId: response?.data?.chat_onboarding?.conversation_id || null
      };
    } catch (error) {
      console.error("Failed to check chat onboarding status:", error);
      throw error;
    }
  };

  // Function to complete an onboarding step
  const completeOnboarding = async (type: OnboardingType): Promise<boolean> => {
    if (!currentWorkspace?.id) {
      console.error("No workspace selected");
      return false;
    }

    try {
      const data = {
        onboarding_type: type,
        status: "completed",
        workspace_id: currentWorkspace.id
      };

      // Note the trailing slash for API compatibility
      const response = await request(
        "/api/onboarding/update_status/",
        {
          method: "POST",
          body: JSON.stringify(data)
        }
      );

      // Refresh onboarding status if successful
      if (response?.status === "success") {
        await getOnboardingStatus(currentWorkspace.id);
      }

      return response?.status === "success" || false;
    } catch (error) {
      console.error(`Failed to complete ${type} onboarding:`, error);
      throw error;
    }
  };

  // Function to skip an onboarding step
  const skipOnboarding = async (type: OnboardingType): Promise<boolean> => {
    if (!currentWorkspace?.id) {
      console.error("No workspace selected");
      return false;
    }

    try {
      const data = {
        onboarding_type: type,
        status: "skipped",
        workspace_id: currentWorkspace.id
      };

      // Note the trailing slash for API compatibility
      const response = await request(
        "/api/onboarding/update_status/",
        {
          method: "POST",
          body: JSON.stringify(data)
        }
      );

      // Refresh onboarding status if successful
      if (response?.status === "success") {
        await getOnboardingStatus(currentWorkspace.id);
      }

      return response?.status === "success" || false;
    } catch (error) {
      console.error(`Failed to skip ${type} onboarding:`, error);
      throw error;
    }
  };

  // Function to update progress for an onboarding step
  const updateProgress = async (
    type: OnboardingType,
    progressData: Record<string, any>
  ): Promise<boolean> => {
    if (!currentWorkspace?.id) {
      console.error("No workspace selected");
      return false;
    }

    try {
      const data = {
        onboarding_type: type,
        status: "in_progress",
        progress: progressData,
        workspace_id: currentWorkspace.id
      };

      // Note the trailing slash for API compatibility
      const response = await request(
        "/api/onboarding/update_status/",
        {
          method: "POST",
          body: JSON.stringify(data)
        }
      );

      return response?.status === "success" || false;
    } catch (error) {
      console.error(`Failed to update ${type} onboarding progress:`, error);
      throw error;
    }
  };

  // Function to start chat onboarding
  const startChatOnboarding = async (): Promise<string | undefined> => {
    if (!currentWorkspace?.id) {
      console.error("No workspace selected");
      return undefined;
    }

    try {
      const data = {
        workspace_id: currentWorkspace.id
      };

      // Note the trailing slash for API compatibility
      const response = await request(
        "/api/chat/start_onboarding/",
        {
          method: "POST",
          body: JSON.stringify(data)
        }
      );

      if (response?.data?.id) {
        setChatConversationId(response.data.id);
        return response.data.id;
      }
    } catch (error) {
      console.error("Failed to start chat onboarding:", error);
      throw error;
    }
  };

  // Initialize onboarding status when workspace changes
  useEffect(() => {
    if (currentWorkspace?.id) {
      const initOnboarding = async () => {
        try {
          const status = await getOnboardingStatus(currentWorkspace.id);

          // Ensure we have the chat conversation ID
          if (status?.chat_onboarding?.conversation_id) {
            setChatConversationId(status.chat_onboarding.conversation_id);
          } else {
            // Double-check with a dedicated API call if needed
            await checkChatOnboardingStatus(currentWorkspace.id);
          }

          setIsInitialized(true);
        } catch (error) {
          console.error("Failed to initialize onboarding:", error);
        }
      };

      initOnboarding();
    }
  }, [currentWorkspace?.id]);

  // Redirect to appropriate onboarding if required
  useEffect(() => {
    if (!isLoading && onboardingStatus && currentWorkspace?.id) {
      // Skip redirects if we're already on an onboarding page
      const path = window.location.pathname;
      if (path.includes("/onboarding")) {
        return;
      }

      // Check and redirect based on onboarding status
      if (onboardingStatus.chat_onboarding?.requires_attention && chatConversationId) {
        // For chat onboarding, navigate to the coach with the conversation ID
        router.push(`/app/${currentWorkspace.id}/coach/${chatConversationId}`);
      } else if (onboardingStatus.ui_onboarding?.requires_attention) {
        // For UI onboarding, we won't redirect as it's handled inline via NextStep
        // Just make sure we're on the main app page
        if (!path.includes("/app")) {
          router.push(`/app/${currentWorkspace.id}`);
        }
      }
    }
  }, [isLoading, onboardingStatus, currentWorkspace?.id, chatConversationId, router]);

  const value = {
    isLoading,
    onboardingStatus,
    isOnboardingRequired: isOnboardingRequired(),
    requiresAttention,
    completeOnboarding,
    skipOnboarding,
    updateProgress,
    chatConversationId,
    startChatOnboarding,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};